<template>
    <div class="content sfc-page">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ t("title") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="pt-0" footer-classes="pb-2">
                <template slot="header">
                    <h3 class="mb-0">{{ t("title") }}</h3>
                    <p class="text-muted small mb-0">{{ t("subtitle") }}</p>
                </template>

                <!-- 503 schema_missing: SQL not run yet → warning + every control disabled (§3.4 / §5.2). -->
                <div v-if="schemaMissing" class="pt-3">
                    <el-alert type="warning" :closable="false" show-icon :title="t('err.schema_missing')" data-testid="sfc-schema-missing"></el-alert>
                </div>

                <el-tabs v-model="tab" class="mt-3" @tab-click="onTab">
                    <!-- ===================== Tab 1: collections ===================== -->
                    <el-tab-pane :label="t('tab_collections')" name="collections">
                        <div class="d-flex justify-content-end mb-3">
                            <base-button type="primary" size="sm" :disabled="locked" data-testid="sfc-create" @click="openCreate">
                                <i class="fa fa-plus"></i> {{ t("create") }}
                            </base-button>
                        </div>

                        <el-table v-loading="loading" :data="rows" class="bon-table bon-tight" row-key="id" header-row-class-name="thead-light" :empty-text="t('empty')" data-testid="sfc-table">
                            <el-table-column :label="t('col.order')" width="96" align="center">
                                <template v-slot="{ row, $index }">
                                    <base-button size="sm" type="neutral" class="p-1" :disabled="locked || reordering || $index === 0" :title="t('move_up')" :aria-label="t('move_up')" @click="reorder($index, -1)"><i class="fa fa-arrow-up"></i></base-button>
                                    <base-button size="sm" type="neutral" class="p-1" :disabled="locked || reordering || $index === rows.length - 1" :title="t('move_down')" :aria-label="t('move_down')" @click="reorder($index, 1)"><i class="fa fa-arrow-down"></i></base-button>
                                </template>
                            </el-table-column>
                            <el-table-column :label="t('col.cover')" width="88">
                                <template v-slot="{ row }">
                                    <img v-if="row.cover_url" :src="row.cover_url" class="sfc-cover" alt="" />
                                    <span v-else class="sfc-cover sfc-cover--empty"></span>
                                </template>
                            </el-table-column>
                            <el-table-column :label="t('col.name')" min-width="160">
                                <template v-slot="{ row }">
                                    <a href="#" class="font-weight-bold" @click.prevent="openEdit(row)">{{ row.name_th || row.name_en }}</a>
                                    <div class="text-muted small">{{ row.name_en }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column :label="t('col.slug')" min-width="200">
                                <template v-slot="{ row }">
                                    <code class="small">/collections/{{ row.slug }}</code>
                                    <base-button size="sm" type="neutral" class="p-1 ml-1" :title="t('copy_url')" :aria-label="t('copy_url')" data-testid="sfc-copy" @click="copyUrl(row.slug)"><i class="fa fa-copy"></i></base-button>
                                </template>
                            </el-table-column>
                            <el-table-column :label="t('col.items')" width="130" align="center">
                                <template v-slot="{ row }">{{ row.item_count || 0 }}</template>
                            </el-table-column>
                            <el-table-column :label="t('col.status')" width="110" align="center">
                                <template v-slot="{ row }">
                                    <el-switch :value="!!row.is_active" :disabled="locked || !!toggling[row.id]" :aria-label="t('col.status')" @change="(v) => toggle(row, v)"></el-switch>
                                </template>
                            </el-table-column>
                            <el-table-column :label="t('col.updated')" min-width="150">
                                <template v-slot="{ row }"><span class="small">{{ fmt(row.updated_at) }}</span></template>
                            </el-table-column>
                            <el-table-column :label="t('col.actions')" width="90" align="center">
                                <template v-slot="{ row }">
                                    <el-dropdown trigger="click" @command="(c) => onAction(c, row)">
                                        <base-button size="sm" type="neutral" class="px-2" :aria-label="t('col.actions')"><i class="fa fa-ellipsis-v"></i></base-button>
                                        <el-dropdown-menu slot="dropdown">
                                            <el-dropdown-item command="edit">{{ t("edit") }}</el-dropdown-item>
                                            <el-dropdown-item command="open">{{ t("open_page") }}</el-dropdown-item>
                                            <el-dropdown-item command="delete" divided :disabled="locked">{{ t("delete") }}</el-dropdown-item>
                                        </el-dropdown-menu>
                                    </el-dropdown>
                                </template>
                            </el-table-column>
                        </el-table>
                    </el-tab-pane>

                    <!-- ===================== Tab 2: discovery sets ===================== -->
                    <el-tab-pane :label="t('tab_discovery')" name="discovery">
                        <div v-loading="discoveryLoading" class="sfc-discovery">
                            <p class="text-muted small">
                                {{ t("discovery_hint") }}
                                <span class="d-block mt-1">{{ t("page_link") }}: <a :href="discoveryUrl" target="_blank" rel="noopener" data-testid="sfc-discovery-link">{{ discoveryUrl }}</a></span>
                            </p>
                            <storefront-collection-form
                                v-if="discoveryForm"
                                :form="discoveryForm"
                                discovery
                                :disabled="locked"
                                :errors="discoveryErrors"
                                :unavailable="discoveryUnavailable"
                            ></storefront-collection-form>
                            <div class="text-right mt-3">
                                <base-button type="primary" size="sm" :loading="discoverySaving" :disabled="locked || !discoveryForm" data-testid="sfc-discovery-save" @click="saveDiscovery">
                                    <i class="fa fa-save"></i> {{ t("save") }}
                                </base-button>
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </card>
        </div>

        <el-dialog :visible.sync="dialogOpen" :title="form && form.id ? t('edit_title') : t('create_title')" width="720px" top="5vh" :close-on-click-modal="false" custom-class="sfc-dialog" append-to-body>
            <div v-loading="dialogLoading">
                <storefront-collection-form
                    v-if="form"
                    :key="formKey"
                    :form="form"
                    :disabled="locked"
                    :errors="errors"
                    :unavailable="unavailable"
                ></storefront-collection-form>
            </div>
            <template slot="footer">
                <base-button type="secondary" size="sm" @click="dialogOpen = false">{{ t("cancel") }}</base-button>
                <base-button type="primary" size="sm" :loading="saving" :disabled="locked || dialogLoading" data-testid="sfc-save" @click="save">{{ t("save") }}</base-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { Alert, Tabs, TabPane, Table, TableColumn, Switch, Dropdown, DropdownMenu, DropdownItem, Dialog, Loading } from "element-ui";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { StorefrontCollectionService } from "@/resource";
import { apiErrorMessage } from "@/util/api-error";
import { fmtBkk } from "@/utils/marketing";
import { storefrontOrigin } from "@/utils/sellerProductRow";
import { copyToClipboard } from "@/utils/clipboard";
import {
    apiCode,
    buildPayload,
    collectionUrl,
    emptyForm,
    DISCOVERY_DEFAULT_NAMES,
    errorKey,
    fieldErrors,
    formFromRow,
    isForbidden,
    isSchemaMissing,
    unavailableFrom,
    validateForm
} from "@/utils/storefrontCollection";
import StorefrontCollectionForm from "./components/StorefrontCollectionForm.vue";

const NO_UNAVAILABLE = () => ({ products: [], bundles: [] });

// ตั้งค่าร้านค้า → คอลเลกชันหน้าเว็บ (spec 2026-10-13-www-nav-i18n-collections §5.2).
// Admin-only (route meta requiresAdmin + menu access isAdmin); the API answers
// 403 access_denied for anyone else and 503 schema_missing until the SQL has been run.
export default {
    name: "StorefrontCollections",
    directives: { loading: Loading.directive },
    components: {
        StorefrontCollectionForm,
        [Alert.name]: Alert,
        [Tabs.name]: Tabs,
        [TabPane.name]: TabPane,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Switch.name]: Switch,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        [Dialog.name]: Dialog
    },
    data() {
        return {
            tab: "collections",
            schemaMissing: false,
            loading: false,
            rows: [],
            reordering: false,
            toggling: {},
            // dialog
            dialogOpen: false,
            dialogLoading: false,
            saving: false,
            form: null,
            formKey: 0,
            errors: {},
            unavailable: NO_UNAVAILABLE(),
            // discovery tab
            discoveryLoaded: false,
            discoveryLoading: false,
            discoverySaving: false,
            discoveryForm: null,
            discoveryErrors: {},
            discoveryUnavailable: NO_UNAVAILABLE()
        };
    },
    computed: {
        ...mapGetters({ isAdmin: "Auth/isAdmin" }),
        locked() {
            return this.schemaMissing || !this.isAdmin;
        },
        discoveryUrl() {
            return collectionUrl(storefrontOrigin(), "");
        }
    },
    created() {
        this.reload();
    },
    methods: {
        t(k, v) {
            return this.$t("storefront_collection." + k, v);
        },
        fmt: fmtBkk,
        // Known codes → our own message; everything else through apiErrorMessage,
        // always WITH the translator (calling it bound — see util/api-error.js).
        notifyError(err) {
            if (isSchemaMissing(err)) {
                this.schemaMissing = true;
                return;
            }
            const code = apiCode(err);
            let message;
            if (isForbidden(err)) message = this.t("err.forbidden");
            else if (errorKey(code)) message = this.t(errorKey(code));
            else message = apiErrorMessage(err, (k) => this.$t(k));
            this.$notify({ type: "danger", message });
        },
        onTab(pane) {
            if (pane.name === "discovery" && !this.discoveryLoaded) this.loadDiscovery();
        },
        async reload() {
            this.loading = true;
            try {
                const res = await StorefrontCollectionService.list();
                this.rows = (res.data && res.data.data) || [];
                this.schemaMissing = false;
            } catch (err) {
                this.rows = [];
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.$notify({ type: "danger", message: this.t("err.load") });
            } finally {
                this.loading = false;
            }
        },
        openCreate() {
            this.form = emptyForm();
            this.formKey += 1;
            this.errors = {};
            this.unavailable = NO_UNAVAILABLE();
            this.dialogOpen = true;
        },
        async loadDetail(id) {
            const res = await StorefrontCollectionService.get(id);
            const data = (res.data && res.data.data) || {};
            this.form = formFromRow(data);
            this.formKey += 1;
            this.unavailable = unavailableFrom(data);
        },
        async openEdit(row) {
            this.form = null;
            this.errors = {};
            this.unavailable = NO_UNAVAILABLE();
            this.dialogOpen = true;
            this.dialogLoading = true;
            try {
                await this.loadDetail(row.id);
            } catch (err) {
                this.dialogOpen = false;
                this.notifyError(err);
            } finally {
                this.dialogLoading = false;
            }
        },
        async save() {
            const errs = validateForm(this.form);
            this.errors = errs;
            if (Object.keys(errs).length) {
                this.$notify({ type: "warning", message: this.t("err.fix_fields") });
                return;
            }
            this.saving = true;
            const isNew = !this.form.id;
            try {
                const body = buildPayload(this.form, { expectedUpdatedAt: isNew ? "" : this.form.updated_at });
                const res = isNew
                    ? await StorefrontCollectionService.create(body)
                    : await StorefrontCollectionService.update(this.form.id, body);
                const data = (res.data && res.data.data) || {};
                this.unavailable = unavailableFrom(data);
                this.dialogOpen = false;
                this.$notify({ type: "success", message: this.t("saved") });
                this.reload();
            } catch (err) {
                const fe = fieldErrors(err);
                if (Object.keys(fe).length) {
                    this.errors = fe;
                    this.$notify({ type: "danger", message: errorKey(apiCode(err)) ? this.t(errorKey(apiCode(err))) : this.t("err.fix_fields") });
                } else if (apiCode(err) === "stale" && this.form.id) {
                    this.notifyError(err);
                    try { await this.loadDetail(this.form.id); } catch (e) { /* keep the form as typed */ }
                } else {
                    this.notifyError(err);
                }
            } finally {
                this.saving = false;
            }
        },
        // The quick switch saves at once: fetch the full row (names, slug, cover,
        // descriptions) and PUT it back without `items`, which keeps the stored list.
        async toggle(row, value) {
            this.$set(this.toggling, row.id, true);
            const before = row.is_active;
            row.is_active = value;
            try {
                const res = await StorefrontCollectionService.get(row.id);
                const data = (res.data && res.data.data) || {};
                const f = formFromRow(data);
                f.is_active = value;
                const saved = await StorefrontCollectionService.update(row.id, buildPayload(f, { withItems: false, expectedUpdatedAt: data.updated_at || "" }));
                const d = (saved.data && saved.data.data) || {};
                if (d.updated_at) row.updated_at = d.updated_at;
                this.$notify({ type: "success", message: this.t("saved") });
            } catch (err) {
                row.is_active = before;
                this.notifyError(err);
            } finally {
                this.$set(this.toggling, row.id, false);
            }
        },
        async reorder(i, dir) {
            const j = i + dir;
            if (j < 0 || j >= this.rows.length) return;
            const ids = this.rows.map((r) => r.id);
            const tmp = ids[i];
            ids[i] = ids[j];
            ids[j] = tmp;
            this.reordering = true;
            try {
                const res = await StorefrontCollectionService.reorder(ids);
                const list = res.data && res.data.data;
                if (Array.isArray(list)) this.rows = list;
                else await this.reload();
                this.$notify({ type: "success", message: this.t("saved") });
            } catch (err) {
                this.notifyError(err);
                this.reload();
            } finally {
                this.reordering = false;
            }
        },
        async copyUrl(slug) {
            const ok = await copyToClipboard(collectionUrl(storefrontOrigin(), slug));
            this.$notify({ type: ok ? "success" : "danger", message: ok ? this.t("copied") : this.t("err.copy_failed") });
        },
        async onAction(cmd, row) {
            if (cmd === "edit") return this.openEdit(row);
            if (cmd === "open") {
                window.open(collectionUrl(storefrontOrigin(), row.slug), "_blank", "noopener");
                return;
            }
            if (cmd !== "delete") return;
            const r = await swal.fire({
                text: this.t("confirm_delete", { name: row.name_th || row.name_en, slug: row.slug }),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.t("confirm"),
                cancelButtonText: this.t("cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            });
            if (!r.value) return;
            try {
                await StorefrontCollectionService.remove(row.id);
                this.$notify({ type: "success", message: this.t("deleted") });
                this.reload();
            } catch (err) {
                this.notifyError(err);
            }
        },
        // ---- discovery sets (singleton) ----
        async loadDiscovery() {
            this.discoveryLoading = true;
            try {
                const res = await StorefrontCollectionService.getDiscovery();
                const data = res.data && res.data.data;
                const f = formFromRow(data);
                // {data:null} until the first save: start from the page's own titles.
                if (!data) Object.assign(f, DISCOVERY_DEFAULT_NAMES);
                this.discoveryForm = f;
                this.discoveryUnavailable = unavailableFrom(data);
                this.discoveryErrors = {};
                this.discoveryLoaded = true;
            } catch (err) {
                this.discoveryForm = this.discoveryForm || emptyForm();
                this.notifyError(err);
            } finally {
                this.discoveryLoading = false;
            }
        },
        async saveDiscovery() {
            const errs = validateForm(this.discoveryForm, { discovery: true });
            this.discoveryErrors = errs;
            if (Object.keys(errs).length) {
                this.$notify({ type: "warning", message: this.t("err.fix_fields") });
                return;
            }
            this.discoverySaving = true;
            try {
                const body = buildPayload(this.discoveryForm, { discovery: true, expectedUpdatedAt: this.discoveryForm.updated_at || "" });
                const res = await StorefrontCollectionService.saveDiscovery(body);
                const data = (res.data && res.data.data) || null;
                if (data) {
                    this.discoveryForm = formFromRow(data);
                    this.discoveryUnavailable = unavailableFrom(data);
                }
                this.$notify({ type: "success", message: this.t("saved") });
            } catch (err) {
                const fe = fieldErrors(err);
                if (Object.keys(fe).length) {
                    this.discoveryErrors = fe;
                    this.$notify({ type: "danger", message: this.t("err.fix_fields") });
                } else if (apiCode(err) === "stale") {
                    this.notifyError(err);
                    this.loadDiscovery();
                } else {
                    this.notifyError(err);
                }
            } finally {
                this.discoverySaving = false;
            }
        }
    }
};
</script>

<style scoped>
.sfc-cover {
    display: inline-block;
    width: 56px;
    height: 42px;
    object-fit: cover;
    border-radius: 4px;
}
.sfc-cover--empty {
    background: #e9ecef;
}
</style>
